import { useState, useCallback, useMemo, useEffect } from "react";
import { PersonData } from "@/entities/people/types";

export interface PeopleSelectionHook {
  selectedPeople: Set<string>;
  selectAll: boolean;
  selectedPeopleData: PersonData[];
  handlePersonSelect: (personId: string, checked: boolean) => void;
  handleSelectAll: (checked: boolean) => void;
  clearSelection: () => void;
}

export const usePeopleSelection = (
  people: PersonData[] | undefined
): PeopleSelectionHook => {
  const [selectedPeople, setSelectedPeople] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(false);

  const handlePersonSelect = useCallback(
    (personId: string, checked: boolean) => {
      setSelectedPeople((prev) => {
        const newSet = new Set(prev);
        if (checked) {
          newSet.add(personId);
        } else {
          newSet.delete(personId);
        }
        return newSet;
      });
    },
    []
  );

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      setSelectAll(checked);
      if (checked) {
        setSelectedPeople(new Set(people?.map((person) => person.id) || []));
      } else {
        setSelectedPeople(new Set());
      }
    },
    [people]
  );

  const clearSelection = useCallback(() => {
    setSelectedPeople(new Set());
    setSelectAll(false);
  }, []);

  const selectedPeopleData = useMemo(() => {
    if (!people) return [];
    return people.filter((person) => selectedPeople.has(person.id));
  }, [people, selectedPeople]);

  // Update selectAll state based on current selection
  useEffect(() => {
    if (people && people.length > 0) {
      const allSelected = people.every((person) =>
        selectedPeople.has(person.id)
      );
      const someSelected = people.some((person) =>
        selectedPeople.has(person.id)
      );
      setSelectAll(allSelected && someSelected);
    }
  }, [selectedPeople, people]);

  return {
    selectedPeople,
    selectAll,
    selectedPeopleData,
    handlePersonSelect,
    handleSelectAll,
    clearSelection
  };
};
